import React, { useEffect, useState } from "react";
import { userAuthHeader } from "../_helpers";
import axios from "axios";
import moment from "moment";
import { getFormJuridique } from "../_helpers";
import ScreenWrapper from "./ScreenWrapper";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import BusinessIcon from "@mui/icons-material/Business";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

// closingDate is stored as YYYY-MM-DD (varchar) but legacy rows may be DD/MM/YYYY.
const parseDate = (value) => {
  if (!value) return null;
  const m = moment(value, ["YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601], true);
  return m.isValid() ? m : null;
};
const formatClosingDate = (value) => {
  const m = parseDate(value);
  return m ? m.format("DD/MM/YYYY") : "N/A";
};
const hasValue = (v) => v !== null && v !== undefined && v !== "" && Number(v) !== 0;
const EUR = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
const formatMoney = (v) => EUR.format(parseFloat(v));
const fmtSiren = (v) => {
  if (!v) return "N/A";
  const s = String(v).replace(/\D/g, "");
  return s.length === 9 ? `${s.slice(0, 3)} ${s.slice(3, 6)} ${s.slice(6)}` : s;
};
// Paris timezone formatters — keep dates/times in French business time regardless of the client's TZ
const FR_DATE = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric" });
const FR_DATETIME = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const formatParisDate = (v) => { if (!v) return "N/A"; const d = new Date(v); return isNaN(d) ? "N/A" : FR_DATE.format(d); };
const formatParisDateTime = (v) => { if (!v) return "N/A"; const d = new Date(v); return isNaN(d) ? "N/A" : FR_DATETIME.format(d).replace(",", "").replace(/(\d{4}) /, "$1 à "); };
const FILING_TYPE_LABELS = { annual_accounts: "Comptes annuels", consolidated_accounts: "Comptes consolidés" };
const LIASSE_TYPE_LABELS = { complete_balance_sheet: "Bilan complet", simplified_balance_sheet: "Bilan simplifié", cash_accounts: "Comptes de trésorerie" };
// Labels mirror prod-deposits-accounts/shared/pricing.ts (PRICING_PACKAGES)
const PACKAGE_TYPE_LABELS = {
  standard: "Dépôt simple",
  late: "Dépôt tardif",
  with_generation: "Dépôt + Génération",
  late_with_generation: "Dépôt tardif + Génération",
  monthly: "Forfait mensuel",
};
// Document types — labels mirror the French wording used in deposits-accounts editors and templates
const DOCUMENT_TYPE_LABELS = {
  meeting_minutes: "Procès-verbal d'approbation des comptes",
  power_attorney_legal_rep: "Procuration du représentant légal",
  privacy_statement: "Déclaration de confidentialité des comptes annuels",
  signed_balance_sheet: "Bilan signé",
  signed_bilan: "Bilan signé",
  synthesis_pj99: "Synthèse PJ99",
  simplified_publication: "Publication simplifiée",
  rejection_details: "Détails du rejet",
};
const labelOf = (map, key) => (key && map[key]) || key || "—";

export default function DepotComptesDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [filing, setFiling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentTab, setDocumentTab] = useState(0);

  useEffect(() => {
    fetchFilingDetails();
  }, [id]);

  const fetchFilingDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}api/user/deposit-accounts/${id}`,
        {
          headers: userAuthHeader(),
        }
      );

      if (response.data && response.data.success) {
        setFiling(response.data.data);
      } else {
        setError("Dépôt de comptes non trouvé");
      }
    } catch (error) {
      console.error("Error fetching filing details:", error);
      setError(
        error.response?.data?.message ||
          "Erreur lors du chargement des détails"
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      draft: "default",
      signed: "info",
      submitted: "primary",
      validated: "success",
      rejected: "error",
      pending: "warning",
    };
    return statusColors[status] || "default";
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      draft: "Brouillon",
      signed: "Signé",
      submitted: "Soumis",
      validated: "Validé",
      rejected: "Rejeté",
      pending: "En attente",
    };
    return statusLabels[status] || status;
  };

  const getPaymentStatusColor = (paymentStatus) => {
    const colors = {
      succeeded: "success",
      monthly: "info",
      pending: "warning",
      failed: "error",
    };
    return colors[paymentStatus] || "default";
  };

  const getPaymentStatusLabel = (paymentStatus) => {
    const labels = {
      succeeded: "Payé",
      monthly: "Forfait mensuel",
      pending: "En attente",
      failed: "Échoué",
    };
    return labels[paymentStatus] || paymentStatus || "En attente";
  };

  if (loading) {
    return (
      <ScreenWrapper>
        <div className="flex w-full h-screen items-center justify-center">
          <CircularProgress />
        </div>
      </ScreenWrapper>
    );
  }

  if (error || !filing) {
    return (
      <ScreenWrapper>
        <div className="flex w-full h-screen items-center justify-center">
          <Alert severity="error">{error || "Dépôt de comptes non trouvé"}</Alert>
        </div>
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <Typography variant="h4" component="h1" className="font-bold">
              Dépôt de comptes #{filing.id}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/userdashboard/depot-comptes")}
              className="text-sm xl:text-base font-medium text-primary hover:text-secondary"
            >
              Retour
            </Button>
          </div>
        </div>

        {/* Status Cards */}
        <Grid container spacing={3} className="mb-6" alignItems="stretch">
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" className="mb-2">
                  Statut du dépôt
                </Typography>
                <Chip
                  label={getStatusLabel(filing.status)}
                  color={getStatusColor(filing.status)}
                  size="large"
                  sx={{ backgroundColor: "#1cbe93", color: "white", alignSelf: "flex-start", mb: 2 }}
                />
                {filing.inpiReferenceNumber && (
                  <Typography variant="body2" className="mt-auto">
                    <strong>Référence INPI&nbsp;:</strong>{" "}
                    {filing.inpiReferenceNumber}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" className="mb-2">
                  Statut du paiement
                </Typography>
                <Chip
                  label={getPaymentStatusLabel(filing.paymentStatus)}
                  color={getPaymentStatusColor(filing.paymentStatus)}
                  size="large"
                  sx={{ backgroundColor: "#1cbe93", color: "white", alignSelf: "flex-start", mb: 2 }}
                />
                <Box className="mt-auto">
                  {filing.amountPaid && (
                    <Typography variant="body2">
                      <strong>Montant&nbsp;:</strong>{" "}
                      {formatMoney(filing.amountPaid)}
                    </Typography>
                  )}
                  {filing.packageType && (
                    <Typography variant="body2">
                      <strong>Forfait&nbsp;:</strong> {labelOf(PACKAGE_TYPE_LABELS, filing.packageType)}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Company Information */}
        <Card className="mb-6">
          <CardContent>
            <Typography variant="h6" className="mb-4 flex items-center gap-2">
              <BusinessIcon /> Informations de l'entreprise
            </Typography>
            <Divider className="mb-4" />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Nom de l'entreprise
                </Typography>
                <Typography variant="body1" className="font-semibold">
                  {filing.companyName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  SIREN
                </Typography>
                <Typography variant="body1" className="font-semibold">
                  {fmtSiren(filing.siren)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Forme juridique
                </Typography>
                <Typography variant="body1">
                  {getFormJuridique(filing.legalForm) || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Adresse
                </Typography>
                <Typography variant="body1">
                  {filing.companyAddress || "N/A"}
                  {filing.companyPostalCode && `, ${filing.companyPostalCode}`}
                  {filing.companyCity && ` ${filing.companyCity}`}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Filing Details */}
        <Card className="mb-6">
          <CardContent>
            <Typography variant="h6" className="mb-4 flex items-center gap-2">
              <ReceiptIcon /> Détails du dépôt
            </Typography>
            <Divider className="mb-4" />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Type de dépôt
                </Typography>
                <Typography variant="body1" className="font-semibold">
                  {labelOf(FILING_TYPE_LABELS, filing.filingType)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Type de liasse
                </Typography>
                <Typography variant="body1" className="font-semibold">
                  {labelOf(LIASSE_TYPE_LABELS, filing.liasseType)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Date de clôture
                </Typography>
                <Typography variant="body1">{formatClosingDate(filing.closingDate)}</Typography>
              </Grid>
              {hasValue(filing.revenue) && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Chiffre d'affaires
                  </Typography>
                  <Typography variant="body1">{formatMoney(filing.revenue)}</Typography>
                </Grid>
              )}
              {hasValue(filing.totalAssets) && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total actif
                  </Typography>
                  <Typography variant="body1">{formatMoney(filing.totalAssets)}</Typography>
                </Grid>
              )}
              {hasValue(filing.totalLiabilities) && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total passif
                  </Typography>
                  <Typography variant="body1">{formatMoney(filing.totalLiabilities)}</Typography>
                </Grid>
              )}
              {hasValue(filing.netIncome) && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Résultat net
                  </Typography>
                  <Typography variant="body1">{formatMoney(filing.netIncome)}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card className="mb-6">
          <CardContent>
            <Typography variant="h6" className="mb-4 flex items-center gap-2">
              <CalendarTodayIcon /> Chronologie
            </Typography>
            <Divider className="mb-4" />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Date de création
                </Typography>
                <Typography variant="body1">{formatParisDate(filing.createdAt)}</Typography>
              </Grid>
              {filing.submittedAt && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date de soumission
                  </Typography>
                  <Typography variant="body1">{formatParisDate(filing.submittedAt)}</Typography>
                </Grid>
              )}
              {filing.paidAt && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date de paiement
                  </Typography>
                  <Typography variant="body1">{formatParisDateTime(filing.paidAt)}</Typography>
                </Grid>
              )}
              {filing.validatedAt && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date de validation
                  </Typography>
                  <Typography variant="body1">{formatParisDateTime(filing.validatedAt)}</Typography>
                </Grid>
              )}
              {filing.rejectionReason && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <Typography variant="body2" color="text.secondary">
                      Raison du rejet
                    </Typography>
                    <Typography variant="body1">
                      {filing.rejectionReason}
                    </Typography>
                    {filing.attachmentinpi && 
                      filing.attachmentinpi.find(att => att.documentType === 'rejection_details') && (
                      <Box className="mt-3">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DownloadIcon />}
                          href={filing.attachmentinpi.find(att => att.documentType === 'rejection_details').fileUrl}
                          target="_blank"
                          sx={{
                            borderColor: '#d32f2f',
                            color: '#d32f2f',
                            '&:hover': {
                              borderColor: '#b71c1c',
                              backgroundColor: 'rgba(211, 47, 47, 0.04)',
                            },
                          }}
                        >
                          Télécharger les détails du rejet
                        </Button>
                      </Box>
                    )}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Documents */}
        {(filing.balanceSheetUrl ||
          filing.incomeStatementUrl ||
          filing.invoiceUrl ||
          (filing.attachments && filing.attachments.length > 0) ||
          (filing.attachmentinpi && filing.attachmentinpi.length > 0)) && (
          <Card className="mb-6">
            <CardContent>
              <Typography
                variant="h6"
                className="mb-4 flex items-center gap-2"
              >
                <AttachFileIcon /> Documents
              </Typography>
              <Divider className="mb-4" />
              
              {/* Document Links */}
              <Grid container spacing={2}>
                {filing.balanceSheetUrl && (
                  <Grid item xs={12} md={6}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      fullWidth
                      href={filing.balanceSheetUrl}
                      target="_blank"
                      className="text-sm xl:text-base font-medium text-primary hover:text-secondary"
                    >
                      Bilan
                    </Button>
                  </Grid>
                )}
                {filing.incomeStatementUrl && (
                  <Grid item xs={12} md={6}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      fullWidth
                      href={filing.incomeStatementUrl}
                      target="_blank"
                      className="text-sm xl:text-base font-medium text-primary hover:text-secondary"
                    >
                      Compte de résultat
                    </Button>
                  </Grid>
                )}
                {filing.invoiceUrl && (
                  <Grid item xs={12} md={6}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      fullWidth
                      href={filing.invoiceUrl}
                      target="_blank"
                      className="text-sm xl:text-base font-medium text-primary hover:text-secondary"
                    >
                      Facture ({filing.invoiceNumber})
                    </Button>
                  </Grid>
                )}
              </Grid>

              {/* Tabs for Attachments */}
              {((filing.attachments && filing.attachments.length > 0) ||
                (filing.attachmentinpi && filing.attachmentinpi.length > 0)) && (
                <Box className="mt-4">
                  <Tabs
                    value={documentTab}
                    onChange={(e, newValue) => setDocumentTab(newValue)}
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      mb: 2,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                      },
                      '& .Mui-selected': {
                        color: '#1cbe93',
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#1cbe93',
                      },
                    }}
                  >
                    <Tab label="Documents téléversés" />
                    <Tab label="Documents INPI" />
                  </Tabs>

                  {/* Tab Panel 1: filing.attachments */}
                  {documentTab === 0 && (
                    <Box>
                      {filing.attachments && filing.attachments.length > 0 ? (
                        <TableContainer component={Paper} sx={{ overflowX: 'auto', width: '100%' }}>
                          <Table sx={{ tableLayout: 'auto', width: '100%' }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ minWidth: '120px' }}>Type de document</TableCell>
                                <TableCell sx={{ minWidth: '200px', maxWidth: '300px' }}>Nom du fichier</TableCell>
                                <TableCell sx={{ minWidth: '120px' }}>Date d'upload</TableCell>
                                <TableCell sx={{ minWidth: '140px' }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filing.attachments.map((attachment) => (
                                <TableRow key={attachment.id}>
                                  <TableCell sx={{ wordBreak: 'break-word' }}>{labelOf(DOCUMENT_TYPE_LABELS, attachment.documentType)}</TableCell>
                                  <TableCell sx={{ wordBreak: 'break-word', maxWidth: '300px' }}>{attachment.fileName}</TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {moment(attachment.uploadedAt).format("DD/MM/YYYY HH:mm")}
                                  </TableCell>
                                  <TableCell>
                                    {attachment.fileUrl && (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<DownloadIcon />}
                                        href={attachment.fileUrl}
                                        target="_blank"
                                        className="text-sm xl:text-base font-medium text-primary hover:text-secondary"
                                      >
                                        Télécharger
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Alert severity="info">Aucun document téléversé</Alert>
                      )}
                    </Box>
                  )}

                  {/* Tab Panel 2: filing.attachmentinpi */}
                  {documentTab === 1 && (
                    <Box>
                      {filing.attachmentinpi && filing.attachmentinpi.length > 0 ? (
                        <TableContainer component={Paper} sx={{ overflowX: 'auto', width: '100%' }}>
                          <Table sx={{ tableLayout: 'auto', width: '100%' }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ minWidth: '120px' }}>Type de document</TableCell>
                                <TableCell sx={{ minWidth: '200px', maxWidth: '300px' }}>Nom du fichier</TableCell>
                                <TableCell sx={{ minWidth: '120px' }}>Date d'upload</TableCell>
                                <TableCell sx={{ minWidth: '140px' }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filing.attachmentinpi.map((attachment, index) => (
                                <TableRow key={attachment.id || index}>
                                  <TableCell sx={{ wordBreak: 'break-word' }}>{attachment.documentType ? labelOf(DOCUMENT_TYPE_LABELS, attachment.documentType) : 'N/A'}</TableCell>
                                  <TableCell sx={{ wordBreak: 'break-word', maxWidth: '300px' }}>{attachment.fileName || 'N/A'}</TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {attachment.uploadedAt ? moment(attachment.uploadedAt).format("DD/MM/YYYY HH:mm") : 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {attachment.fileUrl && (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<DownloadIcon />}
                                        href={attachment.fileUrl}
                                        target="_blank"
                                        className="text-sm xl:text-base font-medium text-primary hover:text-secondary"
                                      >
                                        Télécharger
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Alert severity="info">Aucun document INPI disponible</Alert>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </ScreenWrapper>
  );
}
